import { useLayoutEffect } from 'react';

import { Trans } from '@lingui/react/macro';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

import { GenericError } from '../GenericError/GenericError';
import { MobileSignupFieldTrackingProvider } from './form/MobileSignupFieldTracking';
import { MobileSignupFormProvider } from './form/MobileSignupFormProvider';
import { useFocusOnStep } from './hooks/useFocusOnStep';
import { TNavDirection, useMobileSignupNav } from './hooks/useMobileSignupNav';
import { MobileSignupNavProvider } from './hooks/useMobileSignupNavContext';
import styles from './MobileSignupContainerDE.module.css';
import { MobileSignupLayout } from './MobileSignupLayout/MobileSignupLayout';
import { MobileSignupLoader } from './MobileSignupLoader';
import { SCREEN_REGISTRY } from './screens/registry';
import logger from '../../utils/logger';

const variants = {
  enter: (direction: TNavDirection) => ({
    x: direction === 'forwards' ? '100%' : '-100%',
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: TNavDirection) => ({
    x: direction === 'forwards' ? '-100%' : '100%',
    opacity: 0,
  }),
};

const MobileSignupFlow = () => {
  const nav = useMobileSignupNav();
  const reduceMotion = useReducedMotion();
  const { containerRef, focusHeading } = useFocusOnStep();
  const screenId = nav.status === 'ready' ? nav.screen?.id : undefined;

  // reduced-motion (and jsdom, which mocks useReducedMotion→true) has no animation, so onAnimationComplete won't fire — this covers that case
  useLayoutEffect(() => {
    if (reduceMotion) focusHeading();
  }, [screenId, focusHeading, reduceMotion]);

  if (nav.status === 'pending') {
    return <MobileSignupLoader />;
  }

  const { index, screen, direction, goNext, goBack } = nav;
  const Content = screen?.Content;

  if (!screen || !Content) {
    logger.error('MobileSignup: no screen at index', {
      tags: { team: 'de-introducer' },
      index,
      registryLength: SCREEN_REGISTRY.length,
    });
    return <GenericError errorText={<Trans>Something went wrong.</Trans>} />;
  }

  return (
    <div data-testid="mobile-signup-container">
      <MobileSignupLayout
        progress={{ current: index + 1, total: SCREEN_REGISTRY.length }}
        onBack={goBack}
      >
        <MobileSignupNavProvider goNext={goNext} stage={screen.stage}>
          <MobileSignupFieldTrackingProvider>
            <AnimatePresence mode="wait" custom={direction} initial={false}>
              <motion.div
                key={screen.id}
                ref={containerRef}
                className={styles.animatedStep}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  duration: reduceMotion ? 0 : 0.22,
                  ease: 'easeInOut',
                }}
                onAnimationComplete={(variant) => {
                  if (variant === 'center') focusHeading();
                }}
              >
                <Content />
              </motion.div>
            </AnimatePresence>
          </MobileSignupFieldTrackingProvider>
        </MobileSignupNavProvider>
      </MobileSignupLayout>
    </div>
  );
};

const MobileSignupContainerDE = () => (
  <MobileSignupFormProvider>
    <MobileSignupFlow />
  </MobileSignupFormProvider>
);

export default MobileSignupContainerDE;
